import { Component, OnInit, OnDestroy, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTabsModule } from '@angular/material/tabs';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ExecutionPlanService } from '../../shared/services/execution-plan/execution-plan.service';
import { RoutineDeleteService, routineDeleteErrorMessage } from '../../shared/services/execution-plan/routine-delete.service';
import { ErrorHandlingUtils } from '../../shared/utils/error-handling.utils';
import { RoutineArgumentsUtils } from '../../shared/utils/routine-arguments.utils';
import { ProjectService } from '../../shared/services/project/project.service';
import { AppStateService } from '../../shared/services/app-state/app-state.service';
import { MessageService } from '../../shared/services/message/message.service';
import { DialogService } from '../../shared/services/dialog/dialog.service';
import { LlmService } from '../../shared/services/llm/llm.service';
import { Router } from '@angular/router';
import { Subject, takeUntil, filter, forkJoin, of, catchError } from 'rxjs';
import { parseServerDate } from '../../shared/helpers/date-formatting.helper';
import { LocalstorageHelper } from '../../shared/helpers/localstorage.helper';
import { JOB_PAGE_VIEW_PARAM, JobPageView } from '../../shared/helpers/job-page-view.helper';
import { JOB_HISTORY_PAGE_SIZE } from '../../shared/constants/general.constants';
import { TriggerRoutineRequest, JobListSortColumns } from '../../shared/interfaces/execution-plan.interfaces';
import { EditRoutineDialogData } from '../edit-routine-dialog/edit-routine-dialog.component';
import { JobPatternAnalysisComponent } from '../job-pattern-analysis/job-pattern-analysis.component';
import { RoutineStatsModalData } from '../routine-stats-modal/routine-stats-modal.component';
import {
  PlanDisplayItem,
  JobDisplayItem,
  mapToPlanDisplayItem,
  mapToJobDisplayItem,
  getStatusClass,
  getStatusIcon,
  getActionIcon,
  getStartedSinceIso,
  formatDisplayDate,
  calculateDuration,
  buildFallbackExecPlan,
  jobStatusFilterToApi,
  toDisplayJobStatus,
} from './plan-history.utils';

export { PlanDisplayItem, JobDisplayItem };

export interface PlanHistoryModalData {
  projectId: number;
}

@Component({
  selector: 'app-plan-history-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatTabsModule,
    MatMenuModule,
    MatTooltipModule,
    MatSelectModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    JobPatternAnalysisComponent,
  ],
  templateUrl: './plan-history-modal.component.html',
  styleUrls: ['./plan-history-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanHistoryModalComponent implements OnInit, OnDestroy {
  searchQuery = '';
  selectedStatus = 'Status';
  selectedTimeFilter = 'All Time';
  selectedUser = 'Runs as User';
  selectedTab = signal<'routines' | 'jobs'>('routines');

  plans = signal<PlanDisplayItem[]>([]);
  jobs = signal<JobDisplayItem[]>([]);
  filteredPlans = signal<PlanDisplayItem[]>([]);
  filteredJobs = signal<JobDisplayItem[]>([]);
  loading = signal(false);
  error = signal<string | null>(null);

  planStatusOptions = ['Status', 'Active', 'Paused'];
  jobStatusOptions = ['Status', 'Pending', 'Scheduled', 'Executing', 'Needs Review', 'Completed', 'Failed', 'Canceled', 'Retry', 'Aborted'];
  timeOptions = ['All Time', 'Last 24 hours', 'Last 7 days', 'Last 30 days'];
  userOptions = signal<string[]>(['Runs as User']);

  currentPage = signal(1);
  totalPlans = signal(0);
  totalJobs = signal(0);
  readonly pageSizeOptions: readonly number[] = [12, 25, 50];
  // Persisted so the modal reopens at the user's last choice (EHQ-176).
  itemsPerPage = signal(
    LocalstorageHelper.getListPageSize(JOB_HISTORY_PAGE_SIZE, 12, this.pageSizeOptions)
  );
  selectedPlanId = signal<number | null>(null);
  jobSort: 'status' | 'source' | 'createdAt' | 'scheduledAt' | 'finishedAt' = 'createdAt';
  jobSortDesc = true;

  llmEnabled = signal(false);

  private destroy$ = new Subject<void>();
  private currentVersionId = signal<number | null>(null);

  private executionPlanService = inject(ExecutionPlanService);
  private routineDeleteService = inject(RoutineDeleteService);
  private projectService = inject(ProjectService);
  private appStateService = inject(AppStateService);
  private messageService = inject(MessageService);
  private dialogService = inject(DialogService);
  private readonly llmService = inject(LlmService);
  private router = inject(Router);
  public dialogRef = inject(MatDialogRef<PlanHistoryModalComponent>);
  public data = inject<PlanHistoryModalData>(MAT_DIALOG_DATA);

  // Delegate pure display helpers to template-accessible methods
  readonly getStatusClass = getStatusClass;
  readonly getStatusIcon = getStatusIcon;
  readonly getActionIcon = getActionIcon;

  get selectedRoutineName(): string {
    return this.plans().find(p => p.planId === this.selectedPlanId())?.name ?? '';
  }

  ngOnInit(): void {
    this.loadPlans();
    this.loadCurrentVersionId();
    this.loadLlmStatus();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCurrentVersionId(): void {
    this.projectService.getProject(this.data.projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (project: any) => { this.currentVersionId.set(project.currentVersionId ?? null); },
        error: () => { /* non-critical — HEAD badge just won't show */ },
      });
  }

  private loadLlmStatus(): void {
    this.llmService
      .getOperationalStatus(this.data.projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (status) => { this.llmEnabled.set(status.configured && status.enabled); },
        error: () => { this.llmEnabled.set(false); },
      });
  }

  loadPlans(): void {
    this.loading.set(true);
    this.error.set(null);

    this.executionPlanService.getPlans(this.data.projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (plans) => {
          this.plans.set(plans.map(plan => mapToPlanDisplayItem(plan)));
          this.totalPlans.set(this.plans().length);
          const users = [...new Set(this.plans().map(p => p.runsAsName))];
          this.userOptions.set(['Runs as User', ...users]);
          this.applyFilters();
          if (this.selectedTab() === 'jobs') {
            // Let loadJobs own `loading` so we don't flash an empty table while jobs fetch.
            this.loadJobs();
          } else {
            this.loading.set(false);
            // Refresh job count using the now-populated plans signal to avoid race conditions.
            this.loadJobsCount();
          }
        },
        error: (error) => {
          console.error('Error loading plans:', error);
          this.error.set('Failed to load execution plans');
          this.loading.set(false);
        },
      });
  }

  loadJobs(): void {
    this.loading.set(true);
    this.error.set(null);
    this.jobs.set([]);

    if (this.plans().length === 0) {
      this.filteredJobs.set([]);
      this.totalJobs.set(0);
      this.loading.set(false);
      return;
    }

    // null selectedPlanId means "All Routines"
    if (this.selectedPlanId() !== null) {
      this.loadJobsForPlan(this.selectedPlanId()!);
    } else {
      this.loadJobsForAllPlans();
    }
  }

  private loadJobsForPlan(planId: number): void {
    const runsAsId = this.selectedUser === 'Runs as User'
      ? undefined
      : this.plans().find(p => p.runsAsName === this.selectedUser)?.runsAsId;

    const params = {
      limit: this.itemsPerPage(),
      offset: (this.currentPage() - 1) * this.itemsPerPage(),
      status: jobStatusFilterToApi(this.selectedStatus),
      runsAsId,
      startedSince: getStartedSinceIso(this.selectedTimeFilter),
      sort: this.jobSort as JobListSortColumns,
      desc: this.jobSortDesc,
    };

    this.executionPlanService
      .searchJobs(this.data.projectId, planId, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ items, total }) => {
          const plan = this.plans().find(p => p.planId === planId);
          const planName = plan?.name ?? 'Unknown Plan';
          const runsAsName = plan?.runsAsName ?? 'Unknown User';
          this.jobs.set(items.map(job =>
            mapToJobDisplayItem(job, planName, runsAsName, planId, this.currentVersionId())
          ));
          this.totalJobs.set(total ?? this.jobs().length);
          this.applyFilters();
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading jobs:', error);
          this.error.set('Failed to load jobs');
          this.loading.set(false);
        },
      });
  }

  private loadJobsForAllPlans(): void {
    const plans = this.plans();
    const runsAsId = this.selectedUser === 'Runs as User'
      ? undefined
      : plans.find(p => p.runsAsName === this.selectedUser)?.runsAsId;
    const pageSize = this.itemsPerPage();

    // Fetch a small window from each routine and collect all into one merged page.
    // Each request fetches only the first pageSize items so individual responses stay small.
    const requests = plans.map(plan =>
      this.executionPlanService
        .searchJobs(this.data.projectId, plan.planId, {
          limit: pageSize,
          offset: 0,
          status: jobStatusFilterToApi(this.selectedStatus),
          runsAsId,
          startedSince: getStartedSinceIso(this.selectedTimeFilter),
          sort: this.jobSort as JobListSortColumns,
          desc: this.jobSortDesc,
        })
        .pipe(
          // Catch per-plan errors so a single failure doesn't abort the whole forkJoin batch
          catchError(() => of({ items: [], total: 0 })),
          takeUntil(this.destroy$),
        )
    );

    forkJoin(requests.length ? requests : [of({ items: [], total: 0 })])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          let totalCount = 0;
          const allJobs: JobDisplayItem[] = [];

          results.forEach((result, i) => {
            const plan = plans[i];
            if (!plan) return;
            const { items, total } = result;
            totalCount += total ?? items.length;
            const mapped = items.map(job =>
              mapToJobDisplayItem(job, plan.name, plan.runsAsName, plan.planId, this.currentVersionId())
            );
            allJobs.push(...mapped);
          });

          // Sort across routines according to current jobSort and jobSortDesc
          allJobs.sort((a, b) => {
            let valA: number | string;
            let valB: number | string;
            switch (this.jobSort) {
              case 'scheduledAt':
                valA = a.rawScheduledTime
                  ? parseServerDate(a.rawScheduledTime)?.getTime() ?? 0
                  : (a.rawStartedAt ? parseServerDate(a.rawStartedAt)?.getTime() ?? 0 : (a.rawCreatedAt ? parseServerDate(a.rawCreatedAt)?.getTime() ?? 0 : 0));
                valB = b.rawScheduledTime
                  ? parseServerDate(b.rawScheduledTime)?.getTime() ?? 0
                  : (b.rawStartedAt ? parseServerDate(b.rawStartedAt)?.getTime() ?? 0 : (b.rawCreatedAt ? parseServerDate(b.rawCreatedAt)?.getTime() ?? 0 : 0));
                break;
              case 'createdAt':
                valA = a.rawCreatedAt ? parseServerDate(a.rawCreatedAt)?.getTime() ?? 0 : 0;
                valB = b.rawCreatedAt ? parseServerDate(b.rawCreatedAt)?.getTime() ?? 0 : 0;
                break;
              case 'finishedAt':
                valA = a.rawFinishedAt ? parseServerDate(a.rawFinishedAt)?.getTime() ?? 0 : 0;
                valB = b.rawFinishedAt ? parseServerDate(b.rawFinishedAt)?.getTime() ?? 0 : 0;
                break;
              case 'status':
                valA = (a.status || '').toLowerCase();
                valB = (b.status || '').toLowerCase();
                break;
              case 'source':
                valA = (a.source || '').toLowerCase();
                valB = (b.source || '').toLowerCase();
                break;
              default:
                valA = a.rawCreatedAt ? parseServerDate(a.rawCreatedAt)?.getTime() ?? 0 : 0;
                valB = b.rawCreatedAt ? parseServerDate(b.rawCreatedAt)?.getTime() ?? 0 : 0;
            }
            if (valA < valB) return this.jobSortDesc ? 1 : -1;
            if (valA > valB) return this.jobSortDesc ? -1 : 1;
            return 0;
          });

          // Slice to a single page so pagination metadata stays consistent
          this.jobs.set(allJobs.slice(0, pageSize));
          this.totalJobs.set(totalCount);
          this.applyFilters();
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading jobs across plans:', error);
          this.error.set('Failed to load jobs');
          this.loading.set(false);
        },
      });
  }

  loadJobsCount(): void {
    const plans = this.plans();
    if (plans.length === 0) { this.totalJobs.set(0); return; }

    let totalJobCount = 0;
    let completedRequests = 0;

    plans.forEach(plan => {
      this.executionPlanService.searchJobs(this.data.projectId, plan.planId, { limit: 1 })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: ({ total }) => {
            totalJobCount += total || 0;
            if (++completedRequests === plans.length) this.totalJobs.set(totalJobCount);
          },
          error: (error) => {
            console.error('Error loading job count for plan:', plan.planId, error);
            if (++completedRequests === plans.length) this.totalJobs.set(totalJobCount);
          },
        });
    });
  }

  onSearch(): void {
    this.applyFilters();
  }

  onFilterChange(): void {
    if (this.selectedTab() === 'jobs') {
      this.currentPage.set(1);
      this.loadJobs();
    } else {
      this.applyFilters();
    }
  }

  onTabChange(tab: 'routines' | 'jobs'): void {
    this.selectedTab.set(tab);
    this.selectedStatus = 'Status';
    this.searchQuery = '';
    if (tab === 'jobs') {
      this.currentPage.set(1);
      this.loadJobs();
    } else {
      this.applyFilters();
    }
  }

  /** Jobs are loaded per routine; changing selection refetches the table. */
  onJobsRoutineChange(): void {
    this.currentPage.set(1);
    this.loadJobs();
  }

  applyFilters(): void {
    const q = this.searchQuery.toLowerCase();
    const matchesStatus = (s: string) => this.selectedStatus === 'Status' || s === this.selectedStatus;
    const matchesUser = (u: string) => this.selectedUser === 'Runs as User' || u === this.selectedUser;

    if (this.selectedTab() === 'routines') {
      this.filteredPlans.set(this.plans().filter(plan =>
        (!q || plan.name.toLowerCase().includes(q) || (plan.description ?? '').toLowerCase().includes(q)) &&
        matchesStatus(plan.status) &&
        matchesUser(plan.runsAsName)
      ));
    } else {
      this.filteredJobs.set(this.jobs().filter(job =>
        (!q || job.planName.toLowerCase().includes(q)) &&
        matchesStatus(job.status) &&
        matchesUser(job.runsAsName)
      ));
    }
  }

  onCreateRoutine(): void {
    this.dialogService.addPlanDialog({ projectId: this.data.projectId })
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          this.loadPlans();
        }
      });
  }

  onActionClick(item: PlanDisplayItem | JobDisplayItem, action: string): void {
    if ('schedule' in item) {
      this.handlePlanAction(item as PlanDisplayItem, action);
    } else {
      this.handleJobAction(item as JobDisplayItem, action);
    }
  }

  private handlePlanAction(plan: PlanDisplayItem, action: string): void {
    switch (action) {
      case 'Run Now': this.runPlan(plan); break;
      case 'Pause/Resume': this.togglePlanPaused(plan); break;
      case 'Edit': this.editPlan(plan); break;
      case 'Delete': this.deletePlan(plan); break;
      case 'View Jobs': this.viewPlanJobs(plan); break;
      case 'View Stats': this.viewPlanStats(plan); break;
      case 'View History': this.viewPlanHistory(plan); break;
    }
  }

  private handleJobAction(job: JobDisplayItem, action: string): void {
    switch (action) {
      case 'View Details': this.openJob(job); break;
      case 'View Logs': this.openJob(job, 'logs'); break;
      case 'View Files': this.openJob(job, 'files'); break;
      case 'Cancel': this.cancelJob(job); break;
      case 'Retry': this.retryJob(job); break;
      case 'Refresh Status': this.refreshJobStatus(job); break;
    }
  }

  private editPlan(plan: PlanDisplayItem): void {
    this.executionPlanService.getPlan(this.data.projectId, plan.planId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (execPlan) => {
          const dialogData: EditRoutineDialogData = { projectId: this.data.projectId, routine: execPlan };
          this.dialogService.editRoutineDialog(dialogData).subscribe(result => {
            if (result) this.loadPlans();
          });
        },
        error: () => this.openEditDialogWithFallbackData(plan),
      });
  }

  private openEditDialogWithFallbackData(plan: PlanDisplayItem): void {
    const fullPlan = this.plans().find(p => p.planId === plan.planId);
    if (!fullPlan) { console.error('Plan not found:', plan.planId); return; }

    const dialogData: EditRoutineDialogData = {
      projectId: this.data.projectId,
      routine: buildFallbackExecPlan(fullPlan, this.data.projectId),
    };
    this.dialogService.editRoutineDialog(dialogData).subscribe(result => {
      if (result) this.loadPlans();
    });
  }

  private viewPlanJobs(plan: PlanDisplayItem): void {
    this.selectedPlanId.set(plan.planId);
    this.selectedTab.set('jobs');
    this.currentPage.set(1);
    this.loadJobs();
  }

  /**
   * Open a job's page. View Logs and View Files pass a `view`, and the page
   * opens the failed step's logs or the job's files. Both used to fetch each
   * step in turn and put the raw JSON log lines or file list in a browser
   * alert (EHQ-408).
   *
   * The other query params stay, as the project route needs `versionId` and
   * `workflowId` (SCRUM-340), but `view` is always set or cleared here, so one
   * View Logs does not carry over to the next job opened.
   */
  private openJob(job: JobDisplayItem, view: JobPageView | null = null): void {
    this.dialogRef.close();
    this.router.navigate(
      ['/project', this.data.projectId, 'routine', job.planId, 'job', job.jobId],
      { queryParams: { [JOB_PAGE_VIEW_PARAM]: view }, queryParamsHandling: 'merge' }
    );
  }

  private cancelJob(job: JobDisplayItem): void {
    this.executionPlanService
      .cancelJob(this.data.projectId, job.planId, job.jobId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadJobs(),
        error: (error) => console.error('Failed to cancel job', error),
      });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  get currentStatusOptions(): string[] {
    return this.selectedTab() === 'routines' ? this.planStatusOptions : this.jobStatusOptions;
  }

  get currentItems(): (PlanDisplayItem | JobDisplayItem)[] {
    return this.selectedTab() === 'routines' ? this.filteredPlans() : this.filteredJobs();
  }

  get currentTotal(): number {
    return this.selectedTab() === 'routines' ? this.totalPlans() : this.totalJobs();
  }

  get tabTitle(): string {
    const count = this.selectedTab() === 'routines' ? this.totalPlans() : this.totalJobs();
    return this.selectedTab() === 'routines' ? `ROUTINES (${count})` : `JOBS (${count})`;
  }

  get totalPages(): number {
    return Math.ceil(this.currentTotal / this.itemsPerPage());
  }

  get startItem(): number {
    return (this.currentPage() - 1) * this.itemsPerPage() + 1;
  }

  get endItem(): number {
    return Math.min(this.currentPage() * this.itemsPerPage(), this.currentTotal);
  }

  onItemsPerPageChange(size: number): void {
    this.itemsPerPage.set(size);
    LocalstorageHelper.setListPageSize(JOB_HISTORY_PAGE_SIZE, size);
    this.currentPage.set(1);
    if (this.selectedTab() === 'jobs') {
      this.loadJobs();
    } else {
      this.applyFilters();
    }
  }

  onPreviousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.set(this.currentPage() - 1);
      if (this.selectedTab() === 'jobs') this.loadJobs();
    }
  }

  onNextPage(): void {
    if (this.currentPage() < this.totalPages) {
      this.currentPage.set(this.currentPage() + 1);
      if (this.selectedTab() === 'jobs') this.loadJobs();
    }
  }

  trackByItemId(index: number, item: PlanDisplayItem | JobDisplayItem): number {
    return 'schedule' in item ? (item as PlanDisplayItem).planId : (item as JobDisplayItem).jobId;
  }

  onSort(column: 'status' | 'source' | 'createdAt' | 'scheduledAt' | 'finishedAt'): void {
    if (this.jobSort === column) {
      this.jobSortDesc = !this.jobSortDesc;
    } else {
      this.jobSort = column;
      this.jobSortDesc = true;
    }
    this.loadJobs();
  }

  private runPlan(plan: PlanDisplayItem): void {
    const projectId = this.data.projectId;

    // Fetch workflow to get variable_schema before opening the dialog (mirrors sidebar behaviour).
    if (plan.workflowId && plan.versionId) {
      this.executionPlanService.getWorkflow(projectId, plan.versionId, plan.workflowId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (workflow) => this.openRunDialog(plan, workflow?.variableSchema),
          error: () => this.openRunDialog(plan, undefined),
        });
    } else if (plan.workflowName) {
      const headVersionId = this.appStateService.headVersionId();
      if (headVersionId) {
        this.executionPlanService.getWorkflowByName(projectId, headVersionId, plan.workflowName)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (workflow) => this.openRunDialog(plan, workflow?.variableSchema),
            error: () => this.openRunDialog(plan, undefined),
          });
      } else {
        this.openRunDialog(plan, undefined);
      }
    } else {
      this.openRunDialog(plan, undefined);
    }
  }

  private openRunDialog(plan: PlanDisplayItem, variableSchema?: unknown): void {
    this.dialogService.runNowDialog({
      routineName: plan.name,
      useHeadDefault: !!plan.useHead,
      variableSchema,
      workflowName: plan.workflowName,
      routineArguments: RoutineArgumentsUtils.parse(plan.arguments),
    }).subscribe(result => {
      if (!result) return;

      const body: TriggerRoutineRequest = {
        runs_as_id: this.appStateService.user()?.userId || plan.runsAsId,
        // Send explicit boolean so that unchecking HEAD in the run dialog
        // correctly overrides a HEAD routine's default (use_head=true).
        use_head: result.useHead,
        arguments: result.arguments,
      };

      this.executionPlanService
        .triggerPlan(this.data.projectId, plan.planId, body)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.messageService.showMessage('success', 'Routine triggered successfully');
            this.selectedPlanId.set(plan.planId);
            this.selectedTab.set('jobs');
            this.currentPage.set(1);
            this.loadJobs();
          },
          error: (error) => {
            console.error('Failed to run plan', error);
            this.messageService.showMessage('error', ErrorHandlingUtils.getErrorMessage(error) || 'Failed to trigger routine');
          },
        });
    });
  }

  private deletePlan(plan: PlanDisplayItem): void {
    this.routineDeleteService
      .deleteRoutine(this.data.projectId, plan.planId, plan.name)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.showMessage('success', `Routine "${plan.name}" deleted successfully`);
          this.loadPlans();
        },
        error: (error) => {
          console.error('Failed to delete routine', error);
          this.messageService.showMessage('error', routineDeleteErrorMessage(error));
        },
      });
  }

  private togglePlanPaused(plan: PlanDisplayItem): void {
    const paused = plan.status !== 'Paused';
    const action = paused ? 'pause' : 'resume';
    const actionLabel = paused ? 'Pause' : 'Resume';

    this.dialogService
      .confirm({
        title: `${actionLabel} Routine`,
        message: `${actionLabel} routine "${plan.name}"? ${
          paused
            ? 'This will prevent the routine from running scheduled jobs.'
            : 'This will allow the routine to run scheduled jobs again.'
        }`,
        confirmCaption: actionLabel,
        cancelCaption: 'Cancel',
        isDestructive: false,
      })
      .pipe(
        filter((confirmed) => confirmed),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.loading.set(true);
        this.executionPlanService
          .setPlanPaused(this.data.projectId, plan.planId, paused)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.loading.set(false);
              this.messageService.showMessage('success', `Routine "${plan.name}" ${action}d successfully`);
              this.loadPlans();
            },
            error: (error) => {
              this.loading.set(false);
              this.messageService.showMessage('error', this.getPauseResumeErrorMessage(error, action, plan));
            },
          });
      });
  }

  private getPauseResumeErrorMessage(error: any, action: string, plan: PlanDisplayItem): string {
    const apiError = error?.error?.error?.toLowerCase() ?? '';
    if (apiError.includes('permission')) return 'Permission denied: You do not have permission to pause/resume this routine.';
    if (apiError.includes('not found')) return `Routine not found: project=${this.data.projectId}, routine=${plan.planId}`;
    if (apiError.includes('conflict')) return `Cannot ${action} routine: There may be running jobs that need to be completed first.`;
    return error?.error?.error || error?.message || `Unknown error occurred while trying to ${action} routine.`;
  }

  private retryJob(job: JobDisplayItem): void {
    this.dialogService
      .confirm({
        title: 'Retry Job',
        message: `Retry job #${job.jobId}? This will create a new job with the same parameters.`,
        confirmCaption: 'Retry',
        cancelCaption: 'Cancel',
        isDestructive: false,
      })
      .pipe(
        filter((confirmed) => confirmed),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.loading.set(true);
        this.executionPlanService
          .retryJob(this.data.projectId, job.planId, job.jobId, {})
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (newJob) => {
              this.loading.set(false);
              this.messageService.showMessage('success', `Job retry successful. New job ID: ${newJob.jobId}`);
              this.loadJobs();
            },
            error: (error) => {
              this.loading.set(false);
              this.messageService.showMessage('error', this.getRetryErrorMessage(error, job));
            },
          });
      });
  }

  private getRetryErrorMessage(error: any, job: JobDisplayItem): string {
    const apiError = error?.error?.error?.toLowerCase() ?? '';
    if (apiError.includes('failed or canceled')) return `Cannot retry job: Job must be in Failed or Canceled status. Current status: ${job.status}`;
    if (apiError.includes('not found')) return `Job not found: project=${this.data.projectId}, routine=${job.planId}, job=${job.jobId}`;
    if (apiError.includes('permission')) return 'Permission denied: You do not have permission to retry this job.';
    return error?.error?.error || error?.message || 'Unknown error occurred while retrying job.';
  }

  private refreshJobStatus(job: JobDisplayItem): void {
    const planId = job.planId || this.selectedPlanId();
    if (!planId) {
      this.messageService.showMessage('error', 'Cannot refresh job status: Routine ID is missing. Please reload the jobs list.');
      return;
    }

    this.executionPlanService
      .getJobStatus(this.data.projectId, planId, job.jobId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          const currentJobs = this.jobs();
          const idx = currentJobs.findIndex(j => j.jobId === job.jobId);
          if (idx >= 0) {
            const updatedJobs = [...currentJobs];
            const startedAtStr = updated.startedAt;
            const finishedAtStr = updated.finishedAt;
            updatedJobs[idx] = {
              ...currentJobs[idx],
              status: toDisplayJobStatus(updated.status),
              startedAt: startedAtStr ? formatDisplayDate(startedAtStr) : currentJobs[idx].startedAt,
              rawStartedAt: startedAtStr ?? currentJobs[idx].rawStartedAt,
              finishedAt: finishedAtStr ? formatDisplayDate(finishedAtStr) : currentJobs[idx].finishedAt,
              rawFinishedAt: finishedAtStr ?? currentJobs[idx].rawFinishedAt,
              duration: calculateDuration(startedAtStr ?? currentJobs[idx].rawStartedAt, finishedAtStr ?? currentJobs[idx].rawFinishedAt),
            } as JobDisplayItem;
            this.jobs.set(updatedJobs);
            this.applyFilters();
          }
        },
        error: (error) => this.messageService.showMessage('error', `Failed to refresh job status: ${error.message || 'Unknown error'}`),
      });
  }

  private viewPlanStats(plan: PlanDisplayItem): void {
    const since = getStartedSinceIso(this.selectedTimeFilter);
    this.loading.set(true);
    this.executionPlanService
      .getPlanStats(this.data.projectId, plan.planId, since ? { since } : undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stats) => {
          this.loading.set(false);
          const timeRange = since ? `Last ${this.selectedTimeFilter.toLowerCase()}` : 'All Time';
          const modalData: RoutineStatsModalData = { routineName: plan.name, stats, timeRange };
          this.dialogService.routineStatsModal(modalData).subscribe(() => {});
        },
        error: () => { this.loading.set(false); this.messageService.showMessage('error', 'Failed to load routine statistics. Please try again.'); },
      });
  }

  private viewPlanHistory(plan: PlanDisplayItem): void {
    this.loading.set(true);
    this.executionPlanService
      .getPlanHistory(this.data.projectId, plan.planId, 20)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (history) => {
          this.loading.set(false);
          if (!history || history.length === 0) { alert(`No job history found for routine: ${plan.name}`); return; }

          const lines = [
            `Recent Job History for: ${plan.name}`,
            `Showing last ${history.length} jobs`,
            '',
            ...history.map((h, index) => {
              const when = h.startedAt || h.scheduledTime;
              const duration = h.durationMs ? Math.floor(h.durationMs / 1000) + 's' : '-';
              const stepInfo = h.stepCount > 0 ? ` (${h.completedSteps}/${h.stepCount} steps)` : '';
              return `${index + 1}. ${getStatusIcon(h.status)} ${h.status} - ${when || 'Unknown time'} (${duration})${stepInfo}`;
            }),
          ];
          alert(`Job History:\n\n${lines.join('\n')}`);
        },
        error: () => { this.loading.set(false); this.messageService.showMessage('error', 'Failed to load job history. Please try again.'); },
      });
  }
}
